import { useContext, useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Stack, Grid } from '@mui/material_modern'
import { useGetNegativeInventoryListQuery } from 'services/inventory'
import NegativeInventoryListGrid from 'components/generic/datagrid/NegativeInventoryListGrid'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { FILTER_OPTION_TYPES, PAGE_SIZE } from 'utils/constants'
import { addToDate } from 'utils/data-manipulation/common'

const NegativeInventoryListContainer = () => {
  const context = useContext(ContentLayoutContext)

  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  const { delayRefetch } = useSelector(state => state.inventory)
  const defaultEffectiveDate = useMemo(() => {
    const storedDate = localStorage.getItem('negativeInventoryListEffectiveDate') || '2024-03-31T00:00:00' // use default date of 3/31 as per Gordon
    if (new Date(storedDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()) {
      return addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
    }
    return storedDate
  }, [lockAccountingPeriod])

  const [queryOptions, setQueryOptions] = useState({
    filter: { search: '', effectiveDate: defaultEffectiveDate },
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data: negativeInventoryListData, isFetching } = useGetNegativeInventoryListQuery(
    {
      ...queryOptions,
      filter: JSON.stringify(queryOptions.filter),
    },
    { skip: delayRefetch },
  )

  const transformedRows = (negativeInventoryListData?.Items ?? []).slice().map(inventoryEntity => ({
    id: inventoryEntity?.materialTypeId,
    materialTypeId: inventoryEntity?.materialTypeId,
    materialName: inventoryEntity?.materialTypeCommonName,
    earliestWeight: inventoryEntity?.earliest?.totalNetWeight,
    earliestDate: inventoryEntity?.earliest?.effectiveDate,
    lowestWeight: inventoryEntity?.lowest?.totalNetWeight,
    lowestDate: inventoryEntity?.lowest?.effectiveDate,
    currentWeight: inventoryEntity?.currentNetWeight,
  }))

  const additionalFilterOptions = [
    {
      type: FILTER_OPTION_TYPES.DATE,
      minDate: lockAccountingPeriod.isLockedForAccounting
        ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
        : undefined,
      fromDateName: 'effectiveDate',
      label: 'Start Date',
      width: '200px',
      onChange: date => {
        window.localStorage.setItem('negativeInventoryListEffectiveDate', date?.toISOString() || '')
      },
      defaultFromValue: defaultEffectiveDate,
    },
  ]

  useEffect(() => {
    context.setContainerTitle('Negative Inventory')
  }, [])

  return (
    <Stack>
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <NegativeInventoryListGrid
          rows={transformedRows}
          loading={isFetching || delayRefetch}
          useBackendSortAndFilter
          filter={queryOptions?.filter}
          setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
          rowCount={negativeInventoryListData?.totalResultsCount || 0}
          paginationMode="server"
          onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
          onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
          setQueryOptions={setQueryOptions}
          autoPageSize={false}
          pageSize={queryOptions.pageSize}
          additionalFilterOptions={additionalFilterOptions}
          hideClearButton
        />
      </Grid>
    </Stack>
  )
}

export default NegativeInventoryListContainer
